import type { UndefinedOnPartialDeep } from 'type-fest';

import {
    selectArticleLevelSavings,
    selectAssetSummarySavings,
    selectFamilySavings,
    selectPricesWithoutSalesPersonSavings,
    selectSalesPriceDiscounts,
} from '@repo/api-adapters/selectors/price.selectors';
import { type DtoOrderDiscount, type DtoSaving } from '@repo/api-types/order-retrieval';
import { translate } from '@repo/phrase';

import { useAssetServices } from '@/hooks/asset-services.hooks';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { extractEntitySavings } from '@/utils/services.utils';

const useDeliverySavings = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const assetServices = useAssetServices(data);

    const deliverySavings = assetServices.filter((service) => service.isDelivery).flatMap(extractEntitySavings);

    return summarizeSavings(selectPricesWithoutSalesPersonSavings(deliverySavings));
};

const useFamilySavings = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const salesPriceDiscounts = selectSalesPriceDiscounts(data);
    const familySavingsTotal = useFamilySavingsTotal();
    const deliverySavings = useDeliverySavings();

    const familySavingsNotIncludedInDiscounts = [...selectFamilySavings(data), ...deliverySavings].filter(
        (familySaving) => !isSavingIncludedInDiscounts(familySaving, salesPriceDiscounts)
    );

    if (familySavingsNotIncludedInDiscounts.length > 0) {
        return familySavingsNotIncludedInDiscounts;
    }

    const familySavingsSummary = {
        id: 'family',
        description: translate('asset_price_summary_family_savings'),
        savingValue: familySavingsTotal,
    };

    return [familySavingsSummary];
};

const useFamilySavingsTotal = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const salesPriceDiscounts = selectSalesPriceDiscounts(data);

    return salesPriceDiscounts
        .filter((discount) => discount.discountType === 'FAMILY_PROMOTION')
        .reduce((acc, curr) => acc + (curr.amount ?? 0), 0);
};

const useAssetSummarySavings = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);

    return selectAssetSummarySavings(data);
};

const useArticleLevelSavings = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);

    return selectArticleLevelSavings(data);
};

export const useUniqueSavings = () => {
    const familySavings = useFamilySavings();
    const deliverySavings = useDeliverySavings();
    const articleLevelSavings = useArticleLevelSavings();
    const summarySavings = useAssetSummarySavings();

    const allSavings = [...articleLevelSavings, ...familySavings, ...summarySavings, ...deliverySavings];

    const uniqueSavings = new Set<string>();

    return allSavings.filter((saving) => {
        const key = `${saving.id}:${saving.savingValue}`;

        if (uniqueSavings.has(key)) {
            return false;
        }

        uniqueSavings.add(key);

        return true;
    });
};

const isSavingIncludedInDiscounts = (saving: { id: string; savingValue: number }, discounts: DtoOrderDiscount[]) => {
    return discounts.some(
        (discount) =>
            discount.discountType?.toUpperCase() === saving.id.toUpperCase() && discount.amount === saving.savingValue
    );
};

const summarizeSavings = (savings: Array<UndefinedOnPartialDeep<DtoSaving>>) => {
    return Object.values(
        savings.reduce((acc: Record<string, UndefinedOnPartialDeep<DtoSaving>>, saving) => {
            const key = `${saving.id}-${saving.discountType}`;

            if (!acc[key]) {
                return {
                    ...acc,
                    [key]: { ...saving },
                };
            }

            return {
                ...acc,
                [key]: {
                    ...acc[key],
                    amount: (acc[key].amount ?? 0) + (saving.amount ?? 0),
                },
            };
        }, {})
    ).map(({ amount, description, discountType, id }, index) => ({
        description: description ?? '',
        id: id ?? `${discountType}-${index}`,
        savingValue: amount ?? 0,
    }));
};
